import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { Users, CheckCircle, Clock, Star, Award } from 'lucide-react';
import { motion } from 'framer-motion';

export function SocialProofSection() {
  const testimonials = [
    {
      name: 'Jennifer M.',
      location: 'Ottawa, ON',
      jobs: 31,
      rating: 5,
      text: 'Best $50 I spend every storm. Book it, forget it, wake up to a clear driveway. Every. Single. Time.',
      gradient: 'from-brand-500 to-brand-600',
    },
    {
      name: 'David T.',
      location: 'Montreal, QC',
      jobs: 18,
      rating: 5,
      text: 'Ditched my seasonal contract. SNOWBLOWR is faster, cheaper, way more reliable. Plus I only pay when it snows.',
      gradient: 'from-accent-500 to-accent-600',
    },
    {
      name: 'Emma L.',
      location: 'Ottawa, ON',
      jobs: 24,
      rating: 5,
      text: 'The photo verification is brilliant. I approve from the office and know it\'s done right. No more trust issues.',
      gradient: 'from-brand-600 to-brand-700',
    },
  ];

  return (
    <Section
      eyebrow="Battle-Tested in Canadian Winters"
      title="Real results. Real people. Real storms."
      subtitle="Thousands of driveways cleared across Ottawa and Montreal. Here's what homeowners actually think."
    >
      {/* Stats */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-16 mb-20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <Card variant="solid" className="text-center hover:shadow-card-hover transition-all border-2 border-brand-500/20 hover:border-brand-500 group">
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ duration: 0.3 }}
          >
            <Users className="h-12 w-12 text-brand-500 mx-auto mb-4" strokeWidth={2} />
          </motion.div>
          <p className="text-4xl font-black gradient-text mb-2">100+</p>
          <p className="text-sm font-bold text-neutral-600 uppercase tracking-wider">Neighbours Signed Up</p>
        </Card>

        <Card variant="solid" className="text-center hover:shadow-card-hover transition-all border-2 border-accent-500/20 hover:border-accent-500 group">
          <motion.div
            whileHover={{ scale: 1.1, rotate: -5 }}
            transition={{ duration: 0.3 }}
          >
            <CheckCircle className="h-12 w-12 text-accent-500 mx-auto mb-4" strokeWidth={2} />
          </motion.div>
          <p className="text-4xl font-black text-accent-500 mb-2">99.2%</p>
          <p className="text-sm font-bold text-neutral-600 uppercase tracking-wider">Completion Rate</p>
        </Card>

        <Card variant="solid" className="text-center hover:shadow-card-hover transition-all border-2 border-brand-600/20 hover:border-brand-600 group">
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ duration: 0.3 }}
          >
            <Clock className="h-12 w-12 text-brand-600 mx-auto mb-4" strokeWidth={2} />
          </motion.div>
          <p className="text-4xl font-black text-brand-600 mb-2">2hr</p>
          <p className="text-sm font-bold text-neutral-600 uppercase tracking-wider">Avg Service Time</p>
        </Card>

        <Card variant="solid" className="text-center hover:shadow-card-hover transition-all border-2 border-yellow-500/20 hover:border-yellow-500 group">
          <motion.div
            whileHover={{ scale: 1.1, rotate: -5 }}
            transition={{ duration: 0.3 }}
          >
            <Star className="h-12 w-12 text-yellow-500 mx-auto mb-4" strokeWidth={2} fill="currentColor" />
          </motion.div>
          <p className="text-4xl font-black text-yellow-600 mb-2">4.9/5</p>
          <p className="text-sm font-bold text-neutral-600 uppercase tracking-wider">Customer Rating</p>
        </Card>
      </motion.div>

      {/* Testimonials */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card variant="outline" className="h-full hover:shadow-card-hover hover:border-brand-500/50 transition-all duration-300 group">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${testimonial.gradient} text-white flex items-center justify-center font-black text-lg shadow-lg`}>
                    {testimonial.name.split(' ')[0][0]}{testimonial.name.split(' ')[1][0]}
                  </div>
                  <div>
                    <p className="font-black text-base text-neutral-900">{testimonial.name}</p>
                    <p className="text-xs text-neutral-600 font-medium">{testimonial.location}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-500" fill="currentColor" />
                ))}
              </div>

              <p className="text-sm text-neutral-800 italic leading-relaxed font-medium mb-4">
                "{testimonial.text}"
              </p>

              <div className="pt-4 border-t border-neutral-200 flex items-center gap-2">
                <Award className="w-4 h-4 text-brand-500" />
                <span className="text-xs font-bold text-neutral-600">
                  {testimonial.jobs} jobs completed
                </span>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Press Mentions */}
      <motion.div 
        className="mt-20"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="text-center mb-10">
          <p className="text-sm font-bold text-neutral-500 uppercase tracking-widest mb-2">As Featured In</p>
          <div className="w-20 h-1 bg-gradient-to-r from-brand-500 to-accent-500 mx-auto rounded-full" />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
          {[
            { name: 'Ottawa Citizen', gradient: 'from-neutral-700 to-neutral-900' },
            { name: 'CTV News', gradient: 'from-brand-500 to-brand-600' },
            { name: 'CBC Ottawa', gradient: 'from-accent-500 to-accent-600' },
            { name: 'BetaKit', gradient: 'from-brand-600 to-brand-700' },
          ].map((press, index) => (
            <motion.div
              key={index}
              className="text-center p-6 bg-white rounded-2xl border-2 border-neutral-200 hover:border-brand-500 transition-all hover:shadow-lg group"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.2 }}
            >
              <p className={`text-lg font-black bg-gradient-to-r ${press.gradient} bg-clip-text text-transparent`}>
                {press.name}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </Section>
  );
}

